import React, { useState, useEffect, useCallback } from "react";
import "./AICreatives.scss";
import WelcomeModal from "./components/WelcomeModal";
import CreationForm from "./components/CreationForm";
import BannerPreview from "./components/BannerPreview";
import ResizesModal from "./components/ResizesModal";
import LibrarySection from "./components/LibrarySection";
import { hasVisited, markVisited, getGenerations, saveGeneration, sanitizeFormDataForStorage, updateGeneration } from "./utils/storage";
import { generateBanners, generateResizes } from "./utils/openrouter";

const VIEW = {
  LIBRARY: "library",
  CREATING: "creating",
};

function AICreatives() {
  const [view, setView] = useState(VIEW.LIBRARY);
  const [showWelcome, setShowWelcome] = useState(false);
  const [generations, setGenerations] = useState([]);

  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStep, setGenerationStep] = useState("");
  const [variants, setVariants] = useState([]);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [currentFormData, setCurrentFormData] = useState(null);
  const [currentGenerationId, setCurrentGenerationId] = useState(null);
  const [concept, setConcept] = useState("");

  const [showResizes, setShowResizes] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizes, setResizes] = useState([]);

  const [error, setError] = useState(null);

  useEffect(() => {
    const stored = getGenerations();
    setGenerations(stored);

    if (!hasVisited()) {
      setShowWelcome(true);
    }
  }, []);

  function handleWelcomeClose() {
    markVisited();
    setShowWelcome(false);
  }

  function handleStartCreating() {
    setView(VIEW.CREATING);
    setVariants([]);
    setSelectedVariantId(null);
    setCurrentFormData(null);
    setError(null);
  }

  async function handleFormSubmit(formData) {
    setError(null);
    setIsGenerating(true);
    setCurrentFormData(formData);
    setVariants([]);
    setSelectedVariantId(null);

    const genId = `gen_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    setCurrentGenerationId(genId);

    const newGen = {
      id: genId,
      createdAt: new Date().toISOString(),
      formData: sanitizeFormDataForStorage(formData),
      variants: [],
      selectedVariantId: null,
      resizes: [],
      status: "generating",
    };

    const updatedList = saveGeneration(newGen);
    setGenerations(updatedList);

    try {
      const { variants: result, concept: c } = await generateBanners(formData, setGenerationStep);
      setVariants(result);
      setConcept(c);

      const updated = updateGeneration(genId, { variants: result, status: "done" });
      setGenerations(updated);
    } catch (err) {
      setError(`Ошибка генерации: ${err.message}`);
      const updated = updateGeneration(genId, { status: "error" });
      setGenerations(updated);
    } finally {
      setIsGenerating(false);
      setGenerationStep("");
    }
  }

  async function handleGetResizes() {
    if (!selectedVariantId) return;
    const selected = variants.find((v) => v.id === selectedVariantId);
    if (!selected) return;

    setShowResizes(true);
    setIsResizing(true);
    setResizes([]);

    try {
      const result = await generateResizes(selected, currentFormData, setGenerationStep);
      setResizes(result);

      if (currentGenerationId) {
        const updated = updateGeneration(currentGenerationId, {
          selectedVariantId,
          resizes: result,
        });
        setGenerations(updated);
      }
    } catch (err) {
      setError(`Ошибка создания ресайзов: ${err.message}`);
    } finally {
      setIsResizing(false);
      setGenerationStep("");
    }
  }

  function handleSaveToLibrary() {
    setShowResizes(false);
    setView(VIEW.LIBRARY);
    const stored = getGenerations();
    setGenerations(stored);
  }

  const selectedVariant = variants.find((v) => v.id === selectedVariantId);

  return (
    <div className="AICreatives">
      {showWelcome && <WelcomeModal onClose={handleWelcomeClose} />}

      {showResizes && (
        <ResizesModal
          resizes={resizes}
          isLoading={isResizing}
          selectedVariant={selectedVariant}
          formData={currentFormData}
          onClose={() => setShowResizes(false)}
          onSaveToLibrary={handleSaveToLibrary}
        />
      )}

      <div className="aic-page-header">
        <div className="aic-page-header__left">
          <h1 className="aic-page-header__title">
            <span className="aic-page-header__title-ai">AI</span> Креативы
          </h1>
          <p className="aic-page-header__subtitle">
            Генерация рекламных баннеров с помощью искусственного интеллекта
          </p>
        </div>
        <div className="aic-page-header__actions">
          {view === VIEW.LIBRARY && (
            <button className="aic-btn aic-btn--primary" onClick={handleStartCreating}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
              Создать баннер
            </button>
          )}
          {view === VIEW.CREATING && (
            <button className="aic-btn aic-btn--ghost" onClick={() => setView(VIEW.LIBRARY)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              К библиотеке
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="aic-error-banner">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
            <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          {error}
          <button className="aic-error-banner__close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {view === VIEW.LIBRARY && (
        <LibrarySection
          generations={generations}
          onNewCreative={handleStartCreating}
        />
      )}

      {view === VIEW.CREATING && (
        <div className="aic-creator">
          <div className="aic-creator__form">
            <CreationForm
              onSubmit={handleFormSubmit}
              isLoading={isGenerating}
            />
          </div>
          <div className="aic-creator__preview">
            <BannerPreview
              isLoading={isGenerating}
              currentStep={generationStep}
              variants={variants}
              selectedId={selectedVariantId}
              onSelect={setSelectedVariantId}
              onGetResizes={handleGetResizes}
              formData={currentFormData}
            />
            {concept && !isGenerating && (
              <div className="aic-concept-note">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="#3f73f9" strokeWidth="2"/>
                  <path d="M12 16v-4M12 8h.01" stroke="#3f73f9" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span>{concept}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AICreatives;
